import React, { useContext, useEffect, useRef, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import styled from 'styled-components/native';
import _ from 'lodash';

import { BasicText, FlexBlock, FlexBlockWrapped, MetricStackTitle, PageWrapper, ShinyBlockOuter, ShinyBody, ShinyHeading } from '../Styles';
import { Tabular, TabularColumn } from '../core/Tabular';
import { InputSelect, Search } from '../core';
import { Bound, useMagic } from 'opidcore';
import Icon from '../core/Icon';
import Modal from '../core/Modal';
import InvoiceImporter from './InvoiceImporter';
import Util from '../core/Util';
import { PortalDataContext } from '../PortalContext';

const StyledInputSelect = styled(InputSelect)`
    flex-basis: auto;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
`;

const ListInvoices = ({portalData}) => {
    const portalDataContext = useContext(PortalDataContext);
    const [resultsPerPage, setResultsPerPage] = useState(20);
    const [sortedBy, setSortedBy] = useState("service_period_desc");
    const myBound = useMagic({resultsPerPage: resultsPerPage, sortedBy: sortedBy});
    const [invoices, setInvoices] = useState([]);
    const rawInvoices = useRef([]);

    const [modalVisible, setModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState(null);

    useEffect(()=>{
        if(modalVisible == false){
            setModalContent(null);
        }
    }, [modalVisible]);

    const downloadError = (message) => {
        setModalContent(<BasicText>{message}</BasicText>);
        setModalVisible(true);
    }

    useEffect(()=>{
        if(portalData.invoices != undefined){
            rawInvoices.current = portalData.invoices;
            updateSortOrder(sortedBy, rawInvoices.current);
        }
    }, [portalData.invoices]);

    useEffect( ()=>{
        myBound.magicalState("resultsPerPage", (val)=>setResultsPerPage(val));
        myBound.magicalState("sortedBy", (val)=>setSortedBy(val));
    }, [myBound]);

    useEffect(()=>{
        updateSortOrder(sortedBy);
    }, [sortedBy]);

    const downloadInvoice = (invoiceRow) => {
        if(portalDataContext.authToken != undefined ){
            fetch(Util.getHost() + "/api/v1/portal/canDownloadInvoice?authToken=" + portalDataContext.authToken.code).then((r)=>{
                if(r.status == 200){
                    window.open(Util.getHost() + "/api/v1/portal/downloadInvoice?authToken=" + portalDataContext.authToken.code + "&fileId=" + invoiceRow.file_id + "&arId=" + invoiceRow.invoice_id);
                } else {
                    return r.text();
                }
            }).then((errorMessage)=> {
                if(errorMessage != undefined){
                    downloadError(errorMessage);
                }
            }).catch((e)=>{
                console.log(e);
            });
        } else {
            downloadError("Invalid Login Token.");
        }
    }

    const updateSortOrder = (val, invoiceList) => {
        let newInvoices = undefined;
        if (invoiceList != undefined) {
            newInvoices = invoiceList;
        } else {
            newInvoices = invoices;
        }

        if (newInvoices.length <= 0) {
            return;
        }

        let field = null;

        if (val.indexOf("customer_") > -1) {
            field = "customer_name";
        }

        if (val.indexOf("service_period_") > -1) {
            field = "service_period";
        }

        if (val.indexOf("invoice_date_") > -1) {
            field = "invoice_date";
        }

        if (val.indexOf("ar_status_") > -1) {
            field = "ar_status";
        }

        if (field != null) {
            let sortedInvoices = _.sortBy(newInvoices, field);

            if (val.indexOf("_desc") > -1) {
                sortedInvoices = _.reverse(sortedInvoices);
            }

            setInvoices(sortedInvoices);
        }
    };
    
    const isMobile = Util.isMobile();

    return <PageWrapper>
        <Modal modalVisible={modalVisible} setModalVisible={setModalVisible}>
            {modalContent}
        </Modal>
        {isMobile? null : <InvoiceImporter portalData={portalData}/>}
        <ShinyBlockOuter minHeight={400} flexBasis="99%" flexDirection="row">
            <ShinyHeading>
                <MetricStackTitle>{"Invoices"}</MetricStackTitle>
            </ShinyHeading>
            <ShinyBody>
                <View style={{flexDirection: "row", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 20}}>
                    <Search rawData={rawInvoices.current} setter={(invoiceList)=>updateSortOrder(sortedBy, invoiceList)} />
                    <FlexBlock style={{flexWrap: "wrap", alignItems: "flex-start", justifyContent: "flex-start"}}>
                        <Bound to={myBound}>
                            <StyledInputSelect label="Sort by:" field="sortedBy" options={[
                                { value: "customer_asc", label: "Customer Asc" }, 
                                { value: "customer_desc", label: "Customer Desc" }, 
                                { value: "service_period_asc", label: "Service Period Asc" }, 
                                { value: "service_period_desc", label: "Service Period Desc" }, 
                                { value: "invoice_date_asc", label: "Invoice Date Asc" }, 
                                { value: "invoice_date_desc", label: "Invoice Date Desc" }
                            ]} />
                            <StyledInputSelect label="Results Per Page:" field="resultsPerPage" options={[{value: 10, label: "10"}, {value: 20, label: "20"}, {value: 50, label: "50"}, {value: 0, label: "All"}]}/>
                        </Bound>
                    </FlexBlock>
                </View>
                <Tabular data={invoices} pageSize={resultsPerPage}>
                    <TabularColumn title="Customer" data={(r)=>r.customer_name}/>
                    {isMobile? null : <TabularColumn title="Sites" data={(r)=>r.sites > 1? "Multiple (" + r.sites + ") "  : r.site_names}/>}
                    {isMobile? null : <TabularColumn title="Service Period" data={(r)=>r.service_period}/>}
                    <TabularColumn title="Invoice Date" data={(r)=>r.invoice_date}/>
                    <TabularColumn title="Invoice #" data={(r)=>r.invoice_number}/>
                    <TabularColumn title="Invoice" data={(r)=><TouchableOpacity onPress={()=>downloadInvoice(r)}><Icon icon="file-pdf"/></TouchableOpacity>} style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center"}}/>
                </Tabular>
            </ShinyBody>
        </ShinyBlockOuter>
    </PageWrapper>
}

export default ListInvoices;